import { memo, useMemo } from 'react'

import 'highlight.js/styles/github.css'

import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'

import { MESSAGE_TOOLKIT_EXPORT_ACTIONS } from '../../consts/toolkit-export-actions'
import {
  ActionSelections,
  FlowAction,
  MessageBubbleFollowUpConfig,
} from '../../core/types/chat.types'
import { MaxChatMessage } from '../../types'
import ChartsGrid from '../charts/charts-grid'
import MarkdownRenderer from './markdown-renderer'
import MessageAttachmentsList from './message-attachments-list'
import MessageBubbleFollowUpActions from './message-bubble-follow-up-actions'
import MessageToolkit from './message-toolkit'
import UserAvatar from './user-avatar'

type MessageBubbleProps = {
  message: MaxChatMessage
  followUp?: MessageBubbleFollowUpConfig
  isFavorited?: boolean
  isExportPending?: boolean
  onSelectAction: (payload: {
    action: FlowAction
    message: MaxChatMessage
    selections?: ActionSelections
  }) => void
  onToolkitFavorite: (payload: { message: MaxChatMessage }) => void
  onToolkitExportBudget: (payload: { message: MaxChatMessage; action: FlowAction }) => void
  onToolkitExportReport?: (payload: { message: MaxChatMessage; action: FlowAction }) => void
}

const useStyles = makeStyles((theme) => ({
  userRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    animation: '$slideIn 180ms ease-out',
  },
  userContent: {
    maxWidth: '70%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
  },
  userBubble: {
    borderRadius: '1rem 0.375rem 1rem 1rem',
    padding: '0.625rem 1.25rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
    background: 'var(--color-coral-sender, #E8735A)',
    color: '#fff',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: '0.875rem',
    lineHeight: 1.625,
  },
  assistantRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
    animation: '$slideIn 180ms ease-out',
  },
  assistantRowWide: {
    // When graphs are present, expand to a wider viewport-relative width
    position: 'relative',
    left: '50%',
    width: 'min(64rem, calc(100vw - 8rem))',
    minWidth: 0,
    transform: 'translateX(-50%)',
  },
  contentHost: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  bubble: {
    borderRadius: 16,
    padding: theme.spacing(2),
    wordBreak: 'break-word',
    border: '1px solid var(--ds-border-default, #d7dae4)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    transition: 'background-color 200ms ease',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    background: 'var(--ds-bg-surface, #ffffff)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    '&:hover': {
      background: 'var(--ds-bg-surface-hover, rgba(241, 242, 247, 0.4))',
    },
    '&:has(.html-block-frame)': {
      width: '100%',
    },
  },
  bubbleWide: {
    width: '100%',
    minWidth: 0,
  },
  bubbleBudgetQuestion: {
    borderRadius: '0.375rem 1rem 1rem 1rem',
    padding: '1rem 1.25rem',
    minWidth: 192,
    width: 'fit-content',
  },
  text: {
    color: 'var(--ds-fg-primary, #1f2430)',
    lineHeight: 1.625,
    fontSize: '0.875rem',
  },
  textBudgetQuestion: {
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: 'var(--ds-fg-primary, #1f2430)',
  },
  graphPlaceholder: {
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    fontSize: '0.875rem',
    fontStyle: 'italic',
    animation: '$pulse 1600ms ease-in-out infinite',
  },
  markdown: {
    color: 'var(--ds-fg-primary, #1f2430)',
    lineHeight: 1.625,
    fontSize: '0.875rem',
    overflowWrap: 'anywhere',
    overflowX: 'auto',
    '& > :first-child': { marginTop: 0 },
    '& > :last-child': { marginBottom: 0 },
    '& p': { margin: theme.spacing(1, 0) },
    '& h1': { fontSize: '1.5rem', lineHeight: 1.25, fontWeight: 700, margin: theme.spacing(3, 0, 1.5) },
    '& h2': { fontSize: '1.25rem', lineHeight: 1.3, fontWeight: 700, margin: theme.spacing(2.5, 0, 1.5) },
    '& h3': { fontSize: '1.125rem', lineHeight: 1.35, fontWeight: 700, margin: theme.spacing(2, 0, 1) },
    '& h4, & h5, & h6': { fontSize: '1rem', lineHeight: 1.4, fontWeight: 700, margin: theme.spacing(2, 0, 1) },
    '& ul, & ol': { margin: theme.spacing(1.25, 0), paddingLeft: theme.spacing(3) },
    '& li': { margin: theme.spacing(0.5, 0) },
    '& blockquote': {
      margin: theme.spacing(1.5, 0),
      padding: theme.spacing(0.5, 0, 0.5, 2),
      borderLeft: '4px solid rgba(47, 63, 105, 0.35)',
      color: 'var(--ds-fg-secondary, #535b6c)',
      fontStyle: 'italic',
    },
    '& hr': { margin: theme.spacing(2, 0), border: 0, borderTop: '1px solid var(--ds-border-default, #d7dae4)' },
    '& a': { color: 'var(--color-navy-500, #2f3f69)', textDecoration: 'underline', textUnderlineOffset: 2 },
    '& table': {
      width: 'auto', display: 'table', tableLayout: 'auto', borderCollapse: 'collapse',
      margin: theme.spacing(2, 0), border: '1px solid var(--ds-border-default, #d7dae4)',
      borderRadius: 12, background: 'rgba(255, 255, 255, 0.72)',
    },
    '& thead': { background: 'rgba(47, 63, 105, 0.08)' },
    '& tbody tr:nth-of-type(even)': { background: 'rgba(241, 242, 247, 0.5)' },
    '& th, & td': {
      minWidth: 120, border: '1px solid var(--ds-border-default, #d7dae4)',
      padding: theme.spacing(1, 1.5), textAlign: 'left', verticalAlign: 'top',
    },
    '& th': { fontWeight: 700 },
    '& .md-inline-code': {
      display: 'inline-block', padding: '0.125rem 0.375rem', borderRadius: 6,
      border: '1px solid var(--ds-border-default, #d7dae4)',
      background: 'rgba(241, 242, 247, 0.9)',
      fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace',
      fontSize: '0.82em', lineHeight: 1.4,
    },
    '& .md-code-block': {
      margin: theme.spacing(1.5, 0), overflow: 'hidden', borderRadius: 12,
      border: '1px solid var(--ds-border-default, #d7dae4)', background: '#f6f8fa',
    },
    '& .md-code-header': {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: theme.spacing(1), padding: theme.spacing(0.75, 1.25),
      borderBottom: '1px solid var(--ds-border-default, #d7dae4)', background: 'rgba(255, 255, 255, 0.8)',
    },
    '& .md-code-language': {
      color: 'var(--ds-fg-secondary, #535b6c)', fontSize: '0.75rem', lineHeight: 1.4,
      fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace',
    },
    '& .md-code-copy-btn': {
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 28, height: 28, padding: 0, borderRadius: 8, border: '1px solid transparent',
      background: 'transparent', color: 'var(--ds-fg-tertiary, #8c92a5)', cursor: 'pointer',
      transition: 'all 160ms ease',
      '&:hover': {
        color: 'var(--ds-fg-secondary, #535b6c)',
        background: 'rgba(47, 63, 105, 0.08)',
        borderColor: 'rgba(47, 63, 105, 0.12)',
      },
    },
    '& .md-code-copy-btn.is-copied': {
      color: '#17663a', background: 'rgba(60, 179, 113, 0.12)', borderColor: 'rgba(60, 179, 113, 0.2)',
    },
    '& .md-code-copy-icon': { width: 16, height: 16 },
    '& .md-code-pre': {
      margin: 0, padding: theme.spacing(1.5), overflowX: 'auto',
      fontSize: '0.78rem', lineHeight: 1.6, background: '#f6f8fa',
    },
    '& .md-code-pre code': {
      fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace',
      whiteSpace: 'pre', wordBreak: 'normal',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '1rem',
      '& .md-code-pre': { fontSize: '0.875rem' },
    },
  },
  timestamp: {
    marginTop: theme.spacing(0.5),
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    fontSize: '0.75rem',
  },
  stepLabel: {
    marginTop: theme.spacing(1.5),
    fontSize: '0.625rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--ds-fg-tertiary, #8c92a5)',
  },
  '@keyframes slideIn': {
    from: { opacity: 0, transform: 'translateY(4px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  '@keyframes pulse': {
    '0%': { opacity: 0.55 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0.55 },
  },
}))

const formatTimestamp = (createdAt: number): string =>
  new Date(createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

const MessageBubble = ({
  message,
  followUp,
  isFavorited,
  isExportPending = false,
  onSelectAction,
  onToolkitFavorite,
  onToolkitExportBudget,
  onToolkitExportReport,
}: MessageBubbleProps) => {
  const classes = useStyles()
  const isUserMessage = message.role === 'user'

  const isGraphVisualization = useMemo(() => {
    const actionValue = message.flowMetadata?.selectedAction?.actionValue
    if (actionValue === 'VISUALIZE') return true
    const responseType = message.responseType?.toUpperCase() ?? ''
    return responseType.includes('VISUAL') || responseType.includes('GRAPH')
  }, [message.flowMetadata, message.responseType])

  const looksLikeJson = useMemo(() => {
    const trimmed = message.content.trim()
    if (!trimmed) return false
    return trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('```')
  }, [message.content])

  const hasGraphs = Boolean(message.graphs && message.graphs.length > 0)

  const showGraphPlaceholder = useMemo(() => {
    if (!isGraphVisualization || hasGraphs) return false
    if (!message.content.trim()) return true
    return looksLikeJson
  }, [hasGraphs, isGraphVisualization, looksLikeJson, message.content])

  const hideContent = useMemo(() => {
    if (!isGraphVisualization) return false
    if (showGraphPlaceholder) return true
    return hasGraphs && looksLikeJson
  }, [hasGraphs, isGraphVisualization, looksLikeJson, showGraphPlaceholder])

  const showToolkit = useMemo(() => {
    if (isUserMessage) return false
    const responseType = message.responseType?.toLowerCase() ?? ''
    if (responseType.includes('stream') || responseType.includes('chunk')) return false
    return true
  }, [isUserMessage, message.responseType])

  // Budget export action (CONVERT_EXCEL) from follow-up actions or flow metadata
  const exportAction = useMemo(() => {
    const actions = followUp?.actions ?? message.flowMetadata?.availableActions ?? []
    return actions.find((action) => action.actionValue === 'CONVERT_EXCEL')
  }, [followUp, message.flowMetadata])

  // Message-level export actions always available on AI messages
  const exportActions = useMemo(
    () => MESSAGE_TOOLKIT_EXPORT_ACTIONS.map((action) => ({ ...action })),
    []
  )

  const isBudgetQuestion = !isUserMessage && followUp?.variant === 'budget-question'
  // Show AI timestamp only when no follow-up actions are present
  const showAiTimestamp = !isUserMessage && (!followUp || followUp.actions.length === 0)

  // ── User message ────────────────────────────────────────────────────────────
  if (isUserMessage) {
    return (
      <div className={classes.userRow}>
        <div className={classes.userContent}>
          <div className={classes.userBubble}>
            {message.content}
          </div>
          {message.attachments && message.attachments.length > 0 ? (
            <MessageAttachmentsList attachments={message.attachments} />
          ) : null}
          <Typography variant="caption" className={classes.timestamp}>
            {formatTimestamp(message.createdAt)}
          </Typography>
        </div>
      </div>
    )
  }

  // ── Assistant message ────────────────────────────────────────────────────────
  return (
    <div className={`${classes.assistantRow} ${hasGraphs ? classes.assistantRowWide : ''}`}>
      <UserAvatar type="assistant" />
      <div className={classes.contentHost}>
        {/* Bubble */}
        <div
          className={[
            classes.bubble,
            hasGraphs ? classes.bubbleWide : '',
            isBudgetQuestion ? classes.bubbleBudgetQuestion : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {showGraphPlaceholder ? (
            <span className={classes.graphPlaceholder}>Generating graphs...</span>
          ) : null}

          {!showGraphPlaceholder && !hideContent ? (
            <div className={classes.markdown}>
              <MarkdownRenderer source={message.content} />
            </div>
          ) : null}

          {message.graphs && message.graphs.length > 0 ? (
            <ChartsGrid charts={message.graphs} />
          ) : null}

          {message.attachments && message.attachments.length > 0 ? (
            <MessageAttachmentsList attachments={message.attachments} />
          ) : null}
        </div>

        {/* Toolkit */}
        <MessageToolkit
          message={message}
          isFavorited={Boolean(isFavorited ?? message.fav)}
          showExport={Boolean(exportAction)}
          exportAction={exportAction}
          exportActions={exportActions}
          visible={showToolkit}
          disableAsync={isExportPending}
          onFavorite={(msg) => onToolkitFavorite({ message: msg })}
          onExportBudget={onToolkitExportBudget}
          onExportReport={onToolkitExportReport}
        />

        {/* Timestamp — only when no follow-up actions */}
        {showAiTimestamp ? (
          <Typography variant="caption" className={classes.timestamp}>
            {formatTimestamp(message.createdAt)}
          </Typography>
        ) : null}

        {/* Step label above chips (budget-question variant) */}
        {followUp?.stepLabel ? (
          <p className={classes.stepLabel}>{followUp.stepLabel}</p>
        ) : null}

        {/* Follow-up actions */}
        {followUp && followUp.actions.length > 0 ? (
          <MessageBubbleFollowUpActions
            actions={followUp.actions}
            disabled={followUp.disabled}
            conversationId={followUp.conversationId}
            flowMetadata={followUp.flowMetadata}
            variant={followUp.variant}
            onSelectAction={(action, selections) =>
              onSelectAction({ action, message, selections })
            }
          />
        ) : null}
      </div>
    </div>
  )
}

MessageBubble.displayName = 'MessageBubble'

export default memo(MessageBubble)
